import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const TRUST_IMG =
  "https://images.pexels.com/photos/34823825/pexels-photo-34823825.jpeg?auto=compress&cs=tinysrgb&h=900&w=1400";

const values = [
  {
    title: "Echtes Zuhören",
    text: "Ich höre dir zu – ohne zu bewerten, ohne zu unterbrechen. Manchmal ist genau das der erste Schritt.",
  },
  {
    title: "Absolute Verschwiegenheit",
    text: "Alles, was du mir erzählst, bleibt bei mir. Vertraulichkeit ist die Grundlage unserer Zusammenarbeit.",
  },
  {
    title: "Auf Augenhöhe",
    text: "Du bist die Expertin oder der Experte für dein Leben. Ich begleite dich – ich belehre dich nicht.",
  },
  {
    title: "Ehrlichkeit",
    text: "Ich verspreche dir keine Wunder. Aber ich verspreche dir meine volle Aufmerksamkeit und ehrliches Feedback.",
  },
  {
    title: "Dein Tempo",
    text: "Es gibt keinen festen Fahrplan. Wir gehen so schnell oder so langsam, wie es sich für dich richtig anfühlt.",
  },
];

export default function Trust() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="wichtig" ref={ref} data-testid="trust-section" className="relative py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal className="max-w-2xl mb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#93401D] mb-5">
            Was mir wichtig ist
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.02] text-[#29170C]">
            Fünf Versprechen,
            <br />
            <span className="italic text-[#93401D]">die ich dir gebe.</span>
          </h2>
          <p className="mt-6 text-[#63432B] text-base sm:text-lg leading-relaxed">
            Beratung funktioniert nur mit Vertrauen. Deshalb gilt bei mir – in
            jeder Sitzung, ohne Ausnahme:
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-5 mb-24">
          {values.map((v, i) => (
            <Reveal
              key={v.title}
              delay={i * 0.06}
              className={i < 3 ? "lg:col-span-2" : "lg:col-span-3"}
            >
              <div
                data-testid={`value-card-${i}`}
                className="h-full bg-[#FCF4E8] rounded-3xl border border-[#EAD0AE] p-7 hover:-translate-y-1 hover:shadow-[0_18px_36px_-18px_rgba(60,34,19,0.18)] transition-all duration-500"
              >
                <div className="font-serif text-3xl text-[#C98250] mb-5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-xl text-[#29170C] mb-3">{v.title}</h3>
                <p className="text-[15px] leading-relaxed text-[#63432B]">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Quote block with parallax image */}
        <div className="relative overflow-hidden rounded-[2rem]">
          <motion.img
            style={{ y: imgY, scale: 1.1 }}
            src={TRUST_IMG}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#3C2213]/55 via-[#3C2213]/30 to-transparent" />
          <div className="relative px-8 sm:px-14 py-20 sm:py-32 max-w-3xl">
            <Reveal>
              <Quote className="text-[#F2DFC6]/60 mb-6" strokeWidth={1.2} size={42} />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-[#F2DFC6] tracking-tight">
                Manchmal braucht es nur einen Menschen, der zuhört, um den ersten
                Schritt zu gehen.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 text-sm tracking-[0.2em] uppercase text-[#F2DFC6]/70">
                — Witali Albrecht
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
